import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { instance } from "api/apiBase";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { Applicant } from "types/application";
import {
  BankStatementTransaction,
  OrderBy,
  OrderDirection,
  SelectionMap,
  TransactionsResponse
} from "../types";
import {
  buildTxUrl,
  INIT_SORTING,
  INITIAL_ASSIGNED_PER_PAGE,
  ITEMS_PER_PAGE,
  serializeParams,
  toSelected
} from "../utils";

type Params = {
  applicationId: number;
  verificationId: number;
  applicant: Applicant;
  incomeId?: number;
  filteredSourceIds: number[];
};

export const useEditViewController = ({
  applicationId,
  verificationId,
  applicant,
  incomeId,
  filteredSourceIds
}: Params) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [page, setPage] = useState(1);
  const [sortingState, setSortingState] = useState<SortingState>(INIT_SORTING);
  const [selection, setSelection] = useState<SelectionMap>({});
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const initialSelectionRef = useRef<SelectionMap | null>(null);

  const orderState = sortingState[0] ?? INIT_SORTING[0];
  const orderBy = orderState.id as OrderBy;
  const orderDirection: OrderDirection = orderState.desc ? "desc" : "asc";

  const incomeSourceIdsKey = filteredSourceIds.join(",");

  const selectionIds = useMemo(
    () =>
      Object.keys(selection)
        .map(Number)
        .sort((a, b) => a - b),
    [selection]
  );
  const selectionIdsKey = selectionIds.join(",");

  const transactionsQuery = useQuery({
    queryKey: [
      "APPLICATION_BANK_STATEMENT_TRANSACTIONS",
      applicationId,
      verificationId,
      applicant.id,
      incomeId,
      page,
      ITEMS_PER_PAGE,
      debouncedSearch,
      orderBy,
      orderDirection,
      incomeSourceIdsKey,
      showSelectedOnly,
      showSelectedOnly ? selectionIdsKey : ""
    ],
    queryFn: () =>
      instance
        .get<TransactionsResponse>(buildTxUrl(applicationId, verificationId), {
          params: {
            page,
            per_page: ITEMS_PER_PAGE,
            search: debouncedSearch || undefined,
            order_by: orderBy,
            order_direction: orderDirection,
            applicant_id: applicant.id,
            income_id: incomeId,
            income_source_ids: filteredSourceIds,
            item_ids: showSelectedOnly ? selectionIds : undefined
          },
          paramsSerializer: serializeParams
        })
        .then(({ data }) => data),
    enabled:
      filteredSourceIds.length > 0 &&
      (!showSelectedOnly || selectionIds.length > 0),
    placeholderData: keepPreviousData
  });

  const initialAssignedQuery = useQuery({
    queryKey: [
      "APPLICATION_BANK_STATEMENT_TRANSACTIONS_ASSIGNED",
      applicationId,
      verificationId,
      applicant.id,
      incomeId,
      incomeSourceIdsKey
    ],
    queryFn: () =>
      instance
        .get<TransactionsResponse>(buildTxUrl(applicationId, verificationId), {
          params: {
            per_page: INITIAL_ASSIGNED_PER_PAGE,
            applicant_id: applicant.id,
            income_id: incomeId,
            income_source_ids: filteredSourceIds,
            assignment: "assigned",
            order_by: "amount",
            order_direction: "desc"
          },
          paramsSerializer: serializeParams
        })
        .then(({ data }) => data),
    enabled: Boolean(incomeId) && filteredSourceIds.length > 0
  });

  useEffect(() => {
    if (!initialAssignedQuery.data) return;
    if (initialSelectionRef.current) return;

    const seeded: SelectionMap = {};
    initialAssignedQuery.data.items.forEach((tx) => {
      seeded[tx.id] = toSelected(tx);
    });
    initialSelectionRef.current = seeded;
    setSelection(seeded);
  }, [initialAssignedQuery.data]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, orderBy, orderDirection, showSelectedOnly]);

  useEffect(() => {
    if (showSelectedOnly && Object.keys(selection).length === 0)
      setShowSelectedOnly(false);
  }, [showSelectedOnly, selection]);

  const toggleRow = (tx: BankStatementTransaction) => {
    setSelection((prev) => {
      if (prev[tx.id]) {
        const next = { ...prev };
        delete next[tx.id];
        return next;
      }
      return { ...prev, [tx.id]: toSelected(tx) };
    });
  };

  const hasSelectionChanged = useMemo(() => {
    const initial = initialSelectionRef.current ?? {};
    const initialIds = Object.keys(initial);
    const currentIds = Object.keys(selection);
    if (initialIds.length !== currentIds.length) return true;
    return currentIds.some((id) => !initial[Number(id)]);
  }, [selection]);

  const items: BankStatementTransaction[] = transactionsQuery.data?.items ?? [];
  const totalPages = transactionsQuery.data?.page_count ?? 1;

  return {
    items,
    totalPages,
    isLoading: transactionsQuery.isLoading,
    isEmpty: !transactionsQuery.isLoading && items.length === 0,
    search,
    setSearch,
    page,
    setPage,
    sortingState,
    setSortingState,
    selection,
    selectionIds,
    hasSelectionChanged,
    toggleRow,
    showSelectedOnly,
    setShowSelectedOnly
  };
};
